/**
 * useWhatsAppSendFlow - El flux d'enviar un missatge de WhatsApp d'una reserva
 *
 * L'únic lloc on viu (abans era dins de `WhatsAppActionButton`). El fan servir el botó de la
 * fitxa (`WhatsAppActionButton`, via `MessagingBadge`) i la fila Welcome · Codi · Trucar del
 * popover (`QuickWhatsAppActions`, `plans/dia-a-dia-calendari-i-whatsapp.md` §3.1): tots dos
 * han de deixar el mateix rastre a la fitxa (D5).
 *
 * Flux:
 * 1. `start()`
 * 2. CUSTOM → `MessageComposeSheet` (text lliure)
 * 3. Altrament: crea la tasca (POST /messaging/tasks) → previsualització
 *    - GUEST_FORM (Registre, §7bis): el backend reutilitza el formulari actiu o en crea un (D15);
 *      s'invaliden les consultes amb els `accessTokens` perquè el «Copiar link» el vegi.
 *    - ACCESS: el codi el calcula el backend (F3). Si no ha pogut (avís a `warnings`), cau al
 *      `MessageComposeSheet` per escriure'l a mà, com abans. El compositor rep la tasca creada i
 *      hi posa el codi (`/compose`): no se'n crea una segona, i el «enviat» va a aquesta.
 * 4. Previsualització: text renderitzat + [Obrir WhatsApp]
 * 5. [Obrir WhatsApp] → `openWhatsapp` (format del dispositiu) + heurística de tornada
 * 6. Heurística: timer 60 s + `visibilitychange` → «enviat» optimista, amb «Desmarcar»
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Edit2, Send } from 'lucide-react';
import type { MessageKind } from '@guilleumes/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateMessagingTask, useMarkAsSent, useMarkAsUnsent } from '@/hooks/useMessagingTask';
import {
  guestFormTokenQueryKeys,
  isAccessCodeMissing,
  messageKindEmojis,
  messageKindLabels,
} from '@/lib/messaging';
import { openWhatsapp } from '@/lib/whatsapp-link';
import { MessageComposeSheet } from './MessageComposeSheet';
import { paymentSendBlock } from './messaging-task-card';

interface UseWhatsAppSendFlowOptions {
  reservationId: string;
  messageKind: MessageKind;
  /** Títol del diàleg; per defecte, l'etiqueta del kind («Benvinguda», «Accés»…). */
  title?: string;
  onSent?: () => void;
}

interface WhatsAppSendFlow {
  /** Comença el flux (el clic del botó). */
  start: () => Promise<void>;
  /** Creant la tasca: el botó mostra l'spinner i queda desactivat. */
  isCreating: boolean;
  /** Els diàlegs del flux (previsualització i compositor). S'han de pintar al costat del botó. */
  dialogs: ReactNode;
}

/**
 * Per sobre de tot: la pantalla completa del popover al mòbil és `z-[200]`, i un diàleg a
 * `z-50` quedaria a sota, invisible.
 */
const FLOW_LAYER_CLASS = 'z-[210]';

/** El missatge d'error del backend (Nest: `response.data.message`), si n'hi ha. */
function apiErrorMessage(error: unknown): string | null {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  return null;
}

export function useWhatsAppSendFlow({
  reservationId,
  messageKind,
  title,
  onSent,
}: UseWhatsAppSendFlowOptions): WhatsAppSendFlow {
  const [showPreview, setShowPreview] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  /** Text del backend sense el codi: el compositor d'ACCESS el fa servir de vista prèvia. */
  const [composeBaseText, setComposeBaseText] = useState<string | undefined>(undefined);
  /** La tasca d'ACCESS ja creada: el compositor hi posa el codi en lloc de crear-ne una altra. */
  const [composeTaskId, setComposeTaskId] = useState<string | undefined>(undefined);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [whatsappLink, setWhatsappLink] = useState<string>('');
  const [text, setText] = useState<string>('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  // Pagament amb algun avís (regla ferma): es veu el motiu, però no es pot obrir WhatsApp ni
  // marcar-lo com enviat. L'enllaç i el bloqueig també viuen en refs, perquè els botons els mirin
  // encara que el clic arribi abans del render següent.
  const [sendBlocked, setSendBlocked] = useState(false);
  const sendBlockedRef = useRef(false);
  const whatsappLinkRef = useRef('');

  const queryClient = useQueryClient();
  const createTask = useCreateMessagingTask();
  const markAsSent = useMarkAsSent();
  const markAsUnsent = useMarkAsUnsent();

  // Heurística visibility
  const toastShownRef = useRef(false);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  const cleanupVisibility = useCallback(() => {
    if (visibilityHandlerRef.current) {
      document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
      visibilityHandlerRef.current = null;
    }
  }, []);

  const handleUndo = async (undoTaskId: string) => {
    try {
      await markAsUnsent.mutateAsync(undoTaskId);
      toast.success('Desmarcat correctament');
    } catch {
      toast.error('Error desmarcant');
    }
  };

  const autoMarkSent = async (sentTaskId: string, sentText: string) => {
    toastShownRef.current = true;
    try {
      await markAsSent.mutateAsync({
        taskId: sentTaskId,
        data: {
          sentMessageSnapshot: sentText,
          autoConfirmed: true,
        },
      });
      toast.success(
        <div className="flex flex-col gap-1">
          <span>Missatge marcat com enviat</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleUndo(sentTaskId)}
            className="h-auto py-0 px-2 text-xs underline"
          >
            Desmarcar
          </Button>
        </div>,
        { duration: 10000 },
      );
      onSent?.();
    } catch {
      toast.error('Error marcant com enviat');
    }
  };

  const activateVisibilityHeuristic = (currentTaskId: string, currentText: string) => {
    toastShownRef.current = false;
    cleanupVisibility();

    // Timer 60s
    const timeout = setTimeout(() => {
      if (!toastShownRef.current) {
        cleanupVisibility();
        autoMarkSent(currentTaskId, currentText);
      }
    }, 60000);

    // Visibility change listener
    const handler = () => {
      if (document.hidden) {
        clearTimeout(timeout);
        cleanupVisibility();
        if (!toastShownRef.current) {
          autoMarkSent(currentTaskId, currentText);
        }
      }
    };

    visibilityHandlerRef.current = handler;
    document.addEventListener('visibilitychange', handler);
  };

  const start = async () => {
    if (messageKind === 'CUSTOM') {
      // Text lliure: directament al compositor
      setComposeBaseText(undefined);
      setComposeTaskId(undefined);
      setShowCompose(true);
      return;
    }

    setIsCreating(true);
    try {
      const result = await createTask.mutateAsync({
        reservationId,
        messageKind,
      });

      // Registre: potser hi ha un formulari nou (D15); el «Copiar link» l'ha de veure.
      if (messageKind === 'GUEST_FORM') {
        for (const queryKey of guestFormTokenQueryKeys(reservationId)) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }

      // ACCESS sense codi calculat (§4.7): a escriure'l a mà, com abans de F3.
      if (messageKind === 'ACCESS' && isAccessCodeMissing(result)) {
        setComposeBaseText(result.text);
        setComposeTaskId(result.taskId);
        setShowCompose(true);
        toast.info("No s'ha pogut calcular el codi de la porta: escriu-lo a mà.");
        return;
      }

      // Un missatge de pagament amb avisos o sense enllaç no s'envia: el backend ja no en desa cap
      // (torna un error), i si mai n'arribés un, aquí tampoc s'obre.
      const block = paymentSendBlock(messageKind, result);
      const link = block.length > 0 ? '' : result.whatsappLink;
      sendBlockedRef.current = block.length > 0;
      whatsappLinkRef.current = link;
      setSendBlocked(block.length > 0);
      if (block.length > 0) {
        toast.error('Missatge de pagament no enviable: mira els avisos');
      }

      setTaskId(result.taskId);
      setWhatsappLink(link);
      setText(result.text);
      setWarnings(block.length > 0 ? block : result.warnings);
      setShowPreview(true);
    } catch (error) {
      // El backend explica el perquè (p. ex. «Encara no hi ha missatge de codi per a aquesta propietat»).
      toast.error(apiErrorMessage(error) ?? 'Error creant la tasca de missatgeria');
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenWhatsApp = () => {
    const link = whatsappLinkRef.current || whatsappLink;
    if (sendBlockedRef.current || !link) return;

    openWhatsapp(link);
    setShowPreview(false);

    if (taskId) {
      activateVisibilityHeuristic(taskId, text);
    }
  };

  const handleManualMarkSent = async () => {
    // Un missatge de pagament no enviable no es pot haver enviat.
    if (sendBlockedRef.current) return;
    if (!taskId) return;

    try {
      await markAsSent.mutateAsync({
        taskId,
        data: { sentMessageSnapshot: text },
      });
      toast.success('Marcant com enviat');
      setShowPreview(false);
      cleanupVisibility();
      onSent?.();
    } catch {
      toast.error('Error marcant com enviat');
    }
  };

  const dialogTitle = title ?? messageKindLabels[messageKind];

  const dialogs = (
    <>
      {/* Previsualització (tots els kinds menys CUSTOM i l'ACCESS sense codi) */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className={`sm:max-w-md ${FLOW_LAYER_CLASS}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{messageKindEmojis[messageKind]}</span>
              {dialogTitle}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                <ul className="list-disc list-inside space-y-1">
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Text renderitzat */}
            <div className="bg-gray-50 rounded-md p-4 font-mono text-sm whitespace-pre-wrap">
              {text}
            </div>

            {sendBlocked ? (
              <p role="alert" className="text-xs font-medium text-red-700">
                Missatge de pagament no enviable: arregla el que diuen els avisos i torna-ho a provar.
              </p>
            ) : (
              !whatsappLink && (
                <p className="text-xs text-muted-foreground">
                  El telèfon no és vàlid per a WhatsApp: copia el missatge a mà.
                </p>
              )
            )}

            {/* Accions */}
            <DialogFooter className="flex gap-2 sm:justify-end">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Cancel·lar
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  /* TODO: Editar text */
                }}
                disabled
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Editar
              </Button>
              <Button
                onClick={handleOpenWhatsApp}
                disabled={!whatsappLink || sendBlocked}
                className="bg-green-600 hover:bg-green-700"
              >
                <Send className="h-4 w-4 mr-2" />
                Obrir WhatsApp
              </Button>
            </DialogFooter>

            {/* Mark manual */}
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleManualMarkSent}
                disabled={sendBlocked}
                className="text-muted-foreground"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Ja he enviat el missatge
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compositor: CUSTOM, i ACCESS quan el codi s'ha d'escriure a mà. Només muntat quan cal:
          tancat, cada popover de la graella en demanaria les plantilles. */}
      {showCompose && (
        <MessageComposeSheet
          open={showCompose}
          onOpenChange={setShowCompose}
          reservationId={reservationId}
          messageKind={messageKind}
          baseText={composeBaseText}
          taskId={composeTaskId}
          contentClassName={FLOW_LAYER_CLASS}
          onWhatsappOpened={(openedTaskId, openedText) =>
            activateVisibilityHeuristic(openedTaskId, openedText)
          }
          onDone={() => {
            setShowCompose(false);
          }}
        />
      )}
    </>
  );

  return { start, isCreating, dialogs };
}
